import {
  MouseEvent,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactGA, { EventArgs } from 'react-ga4';
import { I18nKey, Namespace, useTranslation } from 'react-i18next';

import { Clear } from '@gmm/icons';
import {
  OptionsObject,
  SnackbarAction,
  SnackbarKey,
  SnackbarMessage,
  useSnackbar as useNotistackSnackbar,
  VariantType,
  Button,
} from '@gmm/ui';
import { ids as allIds } from '~/lib/constants';

const ids = allIds.snackbar;

type BaseOptions = Omit<OptionsObject, 'action'>;
type SnackbarActionResult = void | PromiseLike<void>;
type SnackbarActionHandler = (
  event: MouseEvent<HTMLButtonElement>,
) => SnackbarActionResult;

interface Options extends BaseOptions {
  action?: {
    label: string;
    onClick: SnackbarActionHandler;
  } | null;
}

export type EnqueueSnackbar = (
  message: SnackbarMessage,
  options?: Options,
) => SnackbarKey;
type EnqueueActionSnackbar = (
  message: SnackbarMessage,
  onClick: SnackbarActionHandler,
  options?: Omit<Options, 'action'>,
) => SnackbarKey;
type MakeVariant = (variant: VariantType) => EnqueueSnackbar;

export interface UseSnackbar {
  enqueueError: EnqueueSnackbar;
  enqueueGenericError: (options?: Options) => SnackbarKey;
  enqueueInfo: EnqueueSnackbar;
  enqueueSnackbar: EnqueueSnackbar;
  enqueueRetrySnackbar: EnqueueActionSnackbar;
  enqueueSuccess: EnqueueSnackbar;
  enqueueUndoSnackbar: EnqueueActionSnackbar;
  enqueueWarning: EnqueueSnackbar;
}

const makeEvent = (label?: string): EventArgs => ({
  category: 'snackbar',
  action: 'clicked',
  label,
});
const closeEvent = makeEvent('close');

export const AUTO_HIDE_AFTER = 4000;
const MAX_RETRIES = 2;
const ERROR_OPTIONS = {
  persist: true,
  preventDuplicate: true,
  variant: 'error' as const,
};

const isPrimitiveMessage = (
  message: SnackbarMessage,
): message is string | number =>
  typeof message === 'string' || typeof message === 'number';

const shouldDedupeErrorByMessage = (
  message: SnackbarMessage,
  options: Options = {},
): boolean =>
  options.preventDuplicate === true &&
  options.variant === 'error' &&
  isPrimitiveMessage(message);

const shouldTrackActiveMessage = (
  message: SnackbarMessage,
  options: Options = {},
): boolean =>
  options.preventDuplicate === true &&
  options.variant === 'error' &&
  (isPrimitiveMessage(message) || options.key !== undefined);

const makeMessageId = (message: string | number): SnackbarKey =>
  `message:${typeof message}:${message}`;

const makeKeyId = (key: SnackbarKey): SnackbarKey => `key:${typeof key}:${key}`;

let nextObjectMessageId = 0;
const objectMessageIds = new WeakMap<object, SnackbarKey>();

const makeObjectMessageId = (message: object): SnackbarKey => {
  const existingId = objectMessageIds.get(message);

  if (existingId !== undefined) {
    return existingId;
  }

  const id = `object:${++nextObjectMessageId}`;

  objectMessageIds.set(message, id);

  return id;
};

const makeLegacyId = (
  message: SnackbarMessage,
  options: Options = {},
): SnackbarKey =>
  options.key || btoa(encodeURIComponent(JSON.stringify({ message, options })));

const makeId = (
  message: SnackbarMessage,
  options: Options = {},
): SnackbarKey => {
  if (
    shouldDedupeErrorByMessage(message, options) &&
    isPrimitiveMessage(message)
  ) {
    return makeMessageId(message);
  }

  if (options.preventDuplicate !== true || options.variant !== 'error') {
    return makeLegacyId(message, options);
  }

  if (options.key !== undefined) {
    return makeKeyId(options.key);
  }

  if (typeof message === 'object' && message !== null) {
    return makeObjectMessageId(message);
  }

  return makeLegacyId(message, options);
};

const makeErrorId = (
  message: SnackbarMessage,
  options: Options = {},
): SnackbarKey => {
  if (isPrimitiveMessage(message)) {
    return makeMessageId(message);
  }

  if (options.key !== undefined) {
    return makeKeyId(options.key);
  }

  if (typeof message === 'object' && message !== null) {
    return makeObjectMessageId(message);
  }

  return makeLegacyId(message, options);
};

const makeNotistackOptions = (
  options: Options,
  trackActiveMessage: boolean,
): Options => {
  if (!trackActiveMessage) {
    return options;
  }

  const { key: _key, ...notistackOptions } = options;

  return {
    ...notistackOptions,
    preventDuplicate: false,
  };
};

interface ActiveSnackbar {
  action: Options['action'];
  key: SnackbarKey;
  listeners: Set<() => void>;
  version: number;
}

interface SnackbarState {
  activeMessages: Map<SnackbarKey, ActiveSnackbar>;
  pendingRetries: Map<SnackbarKey, Set<symbol>>;
  retryCounts: Map<SnackbarKey, number>;
}

type NotistackEnqueueSnackbar = ReturnType<
  typeof useNotistackSnackbar
>['enqueueSnackbar'];

const snackbarStateByProvider = new WeakMap<
  NotistackEnqueueSnackbar,
  SnackbarState
>();

const getSnackbarState = (
  enqueueSnackbar: NotistackEnqueueSnackbar,
): SnackbarState => {
  const state = snackbarStateByProvider.get(enqueueSnackbar);

  if (state) {
    return state;
  }

  const nextState: SnackbarState = {
    activeMessages: new Map(),
    pendingRetries: new Map(),
    retryCounts: new Map(),
  };

  snackbarStateByProvider.set(enqueueSnackbar, nextState);

  return nextState;
};

interface SnackbarActionsProps {
  activeMessages: Map<SnackbarKey, ActiveSnackbar>;
  closeSnackbar: (key: SnackbarKey) => void;
  closeLabel: string;
  id: SnackbarKey;
  inheritActionColor: boolean;
  initialAction: Options['action'];
  snackbarKey: SnackbarKey;
  trackActiveMessage: boolean;
}

const SnackbarActions = ({
  activeMessages,
  closeSnackbar,
  closeLabel,
  id,
  inheritActionColor,
  initialAction,
  snackbarKey,
  trackActiveMessage,
}: SnackbarActionsProps): ReactElement => {
  const [, setVersion] = useState(0);
  const activeMessage = trackActiveMessage ? activeMessages.get(id) : undefined;
  const action = activeMessage ? activeMessage.action : initialAction;
  const renderedVersion = activeMessage?.version;

  useEffect(() => {
    if (!trackActiveMessage) {
      return undefined;
    }

    const subscribedMessage = activeMessages.get(id);

    if (!subscribedMessage) {
      return undefined;
    }

    const refresh = (): void => setVersion(version => version + 1);

    subscribedMessage.listeners.add(refresh);

    if (subscribedMessage.version !== renderedVersion) {
      refresh();
    }

    return () => {
      subscribedMessage.listeners.delete(refresh);
    };
  }, [activeMessages, id, renderedVersion, trackActiveMessage]);

  const label = action?.label ?? '';
  const handleAction: MouseEventHandler<HTMLButtonElement> = event => {
    ReactGA.event(makeEvent(label));

    if (trackActiveMessage && activeMessages.get(id)?.key === snackbarKey) {
      activeMessages.delete(id);
    }

    action?.onClick(event);
    closeSnackbar(snackbarKey);
  };
  const handleClose = (): void => {
    ReactGA.event(closeEvent);

    if (trackActiveMessage && activeMessages.get(id)?.key === snackbarKey) {
      activeMessages.delete(id);
    }

    closeSnackbar(snackbarKey);
  };

  return (
    <>
      {action && (
        <Button
          color={inheritActionColor ? 'inherit' : undefined}
          data-testid={ids.actionButton}
          onClick={handleAction}
        >
          {label}
        </Button>
      )}
      <Button
        aria-label={closeLabel}
        color="inherit"
        data-testid={ids.closeButton}
        onClick={handleClose}
      >
        <Clear />
      </Button>
    </>
  );
};

interface Options {
  autoTranslate?: boolean;
}

export const useSnackbar = ({ autoTranslate }: Options = {}): UseSnackbar => {
  const [t] = useTranslation<Namespace>();
  const { closeSnackbar, enqueueSnackbar: notistackEnqueueSnackbar } =
    useNotistackSnackbar();
  const { current: snackbarState } = useRef(
    getSnackbarState(notistackEnqueueSnackbar),
  );
  const { activeMessages, pendingRetries, retryCounts } = snackbarState;
  const lastMessage = useRef<{
    id: SnackbarKey;
    key: SnackbarKey;
    message: SnackbarMessage;
    options: Options;
  } | null>(null);

  const { current: enqueueSnackbar } = useRef<EnqueueSnackbar>(
    (message, options = {}) => {
      const id = makeId(message, options);
      const trackActiveMessage = shouldTrackActiveMessage(message, options);
      const action: SnackbarAction = (key: SnackbarKey) => (
        <SnackbarActions
          activeMessages={activeMessages}
          closeSnackbar={closeSnackbar}
          closeLabel={t('app:buttons.close')}
          id={id}
          inheritActionColor={options.variant === 'error'}
          initialAction={options.action}
          snackbarKey={key}
          trackActiveMessage={trackActiveMessage}
        />
      );

      const activeMessage = trackActiveMessage
        ? activeMessages.get(id)
        : undefined;

      if (activeMessage !== undefined) {
        if (activeMessage.action !== options.action) {
          activeMessage.action = options.action;
          activeMessage.version += 1;
          activeMessage.listeners.forEach(listener => listener());
        }

        return activeMessage.key;
      }

      if (lastMessage.current?.id === id && !options.preventDuplicate) {
        closeSnackbar(lastMessage.current?.key);
      }

      const notistackOptions = makeNotistackOptions(
        options,
        trackActiveMessage,
      );
      const nextActiveMessage: ActiveSnackbar | undefined = trackActiveMessage
        ? {
            action: options.action,
            key: id,
            listeners: new Set(),
            version: 0,
          }
        : undefined;

      if (nextActiveMessage) {
        activeMessages.set(id, nextActiveMessage);
      }

      const key = notistackEnqueueSnackbar(message, {
        anchorOrigin: {
          horizontal: 'left',
          vertical: 'bottom',
        },
        ...notistackOptions,
        action,
        onExited: (event, exitedKey) => {
          options.onExited?.(event, exitedKey);

          const activeMessage = activeMessages.get(id);
          const exitedActiveMessage = activeMessage?.key === exitedKey;

          if (exitedActiveMessage) {
            activeMessages.delete(id);
          }

          if (
            !pendingRetries.has(id) &&
            (activeMessage === undefined || exitedActiveMessage)
          ) {
            retryCounts.delete(id);
          }

          if (lastMessage.current?.key === exitedKey) {
            lastMessage.current = null;
          }
        },
      });

      lastMessage.current = {
        id,
        key,
        message,
        options,
      };

      if (nextActiveMessage) {
        nextActiveMessage.key = key;
      }

      return lastMessage.current.key;
    },
  );

  const makeVariant: MakeVariant = variant => (message, options) =>
    enqueueSnackbar(
      autoTranslate && typeof message === 'string'
        ? t(message as I18nKey)
        : message,
      {
        ...options,
        ...(variant === 'error' ? ERROR_OPTIONS : {}),
      },
    );

  const { current: enqueueError } = useRef(makeVariant('error'));
  const { current: enqueueInfo } = useRef(makeVariant('info'));
  const { current: enqueueSuccess } = useRef(makeVariant('success'));
  const { current: enqueueWarning } = useRef(makeVariant('warning'));

  const { current: enqueueUndoSnackbar } = useRef<EnqueueActionSnackbar>(
    (message, onClick, options = {}) =>
      enqueueSuccess(message, {
        ...options,
        action: { label: t('app:undo'), onClick },
      }),
  );

  const { current: enqueueRetrySnackbar } = useRef<EnqueueActionSnackbar>(
    (message, onClick, options = {}) => {
      const id = makeErrorId(message, options);
      const retryCount = (retryCounts.get(id) ?? 0) + 1;

      retryCounts.set(id, retryCount);

      if (retryCount > MAX_RETRIES) {
        return enqueueError(message, options);
      }

      const handleRetry: SnackbarActionHandler = event => {
        const pendingRetry = Symbol();
        const retries = pendingRetries.get(id) ?? new Set<symbol>();

        retries.add(pendingRetry);
        pendingRetries.set(id, retries);

        const finishRetry = (): void => {
          const currentRetries = pendingRetries.get(id);

          if (!currentRetries?.delete(pendingRetry)) {
            return;
          }

          if (currentRetries.size === 0) {
            pendingRetries.delete(id);

            if (!activeMessages.has(id)) {
              retryCounts.delete(id);
            }
          }
        };

        try {
          const result = onClick(event);

          if (
            typeof result === 'object' &&
            result !== null &&
            'then' in result
          ) {
            void Promise.resolve(result).finally(finishRetry);
          } else {
            finishRetry();
          }
        } catch (error) {
          finishRetry();
          throw error;
        }
      };

      return enqueueError(message, {
        ...options,
        action: { label: t('app:retry'), onClick: handleRetry },
      });
    },
  );

  const { current: enqueueGenericError } = useRef<
    (options?: Options) => SnackbarKey
  >((options: Options = {}) =>
    enqueueSnackbar(t('app:error.snackbar'), {
      ...options,
      ...ERROR_OPTIONS,
    }),
  );

  const { current: snackbarActions } = useRef({
    enqueueError,
    enqueueGenericError,
    enqueueInfo,
    enqueueRetrySnackbar,
    enqueueSnackbar,
    enqueueSuccess,
    enqueueUndoSnackbar,
    enqueueWarning,
  });

  return snackbarActions;
};
